/**
 * LLM model selection — shared client for the header picker.
 *
 * The catalogue comes from the server (backend/llm_providers.py) rather than
 * being duplicated here, so adding a model is a one-file change and the
 * dropdown can never offer something the backend won't route.
 *
 * Cached for the page's lifetime: the header renders on every route change and
 * the selection changes about once a month. `invalidateLLMSettings()` after a
 * save is what keeps the chip honest.
 */

import { api } from "./api";

export interface LLMRole {
  role: string;
  label: string;
  provider: string;
  model: string;
  modelLabel: string;
  known: boolean;
  configured: boolean;
  /** Which of four sources decided this role: "picker" | "config_yaml" |
   *  "fallback" | "default". Not the same question as `known` — see below. */
  source: string;
  /** That source, said in words, from the backend so the two cannot drift. */
  sourceLabel: string;
  /** The roles walked to get here. One entry means it was decided on its own;
   *  ["brief_daily","brief","full_analysis"] means the daily brief is running
   *  pass 2's model because neither brief role was ever set. */
  via: string[];
  /** A lower-priority source naming a different model — config.yaml under a
   *  picker setting. null on a normal install. This is the state that cost a
   *  week of daily briefs while looking correct from every screen. */
  shadowedBy: string | null;
  shadowedModel: string | null;
}

export interface LLMProvider {
  id: string;
  label: string;
  free: boolean;
  configured: boolean;
}

export interface LLMModel {
  id: string;
  provider: string;
  label: string;
  note: string;
  free: boolean;
  roles: string[];
}

export interface LLMSettings {
  roles: LLMRole[];
  providers: LLMProvider[];
  catalogue: LLMModel[];
  canEdit: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  /**
   * Set when the combination of roles is broken in a way no single role's
   * `configured` flag conveys — see _combination_warning in backend/routers/llm.py.
   */
  warning?: string | null;
}

let _cached: LLMSettings | null = null;
let _inFlight: Promise<LLMSettings> | null = null;

export function invalidateLLMSettings(): void {
  _cached = null;
  _inFlight = null;
}

export async function getLLMSettings(force = false): Promise<LLMSettings> {
  if (force) invalidateLLMSettings();
  if (_cached) return _cached;
  // Share one request between concurrent callers — the header and a page can
  // both ask on the same route change.
  if (!_inFlight) {
    _inFlight = api<LLMSettings>("/llm/settings")
      .then((s) => {
        _cached = s;
        return s;
      })
      .finally(() => {
        _inFlight = null;
      });
  }
  return _inFlight;
}

export async function saveLLMSettings(body: {
  relevanceCheck?: string;
  fullAnalysis?: string;
  agent?: string;
  /** The shared fallback both cadences follow when unset. */
  brief?: string;
  briefDaily?: string;
  briefWeekly?: string;
  /** Reads the unassigned pile and proposes taxonomy changes. */
  watchAreaReview?: string;
  /** Rolling story summaries, written between briefs. */
  storySummary?: string;
  /** The weekly's final pass: bulk summary mop-up, then section writing. */
  weeklyMap?: string;
  weeklySynthesis?: string;
  /** Whole-corpus classification. The one role held out of the end of a
   *  model's daily allowance, because its work is never urgent. */
  backfill?: string;
}): Promise<LLMSettings> {
  const updated = await api<LLMSettings>("/llm/settings", { method: "PUT", body });
  _cached = updated;
  return updated;
}

/** The model shown on the header chip: pass 2 is the one that writes the signals. */
export function primaryRole(settings: LLMSettings): LLMRole | undefined {
  return settings.roles.find((r) => r.role === "full_analysis") ?? settings.roles[0];
}
